import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { CONTACT_LENS_MODALITIES } from '@lensmira/domain'

import { Ltr, useTranslation } from '../i18n.js'
import { sentence } from '../sentence.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { DataSurface } from './DataSurface.js'

/**
 * The contact lens catalogue. §6.4.3 `CAT-020`, `CAT-021`, `CAT-022`, `CAT-031`.
 *
 * **`cl_product` was 0 rows in all fourteen production tenants.** The API could create, correct,
 * stop and resume a contact lens, and nothing a shopkeeper can open called any of it, so a box of
 * contact lenses could not be received against a product that did not exist.
 *
 * ---
 *
 * **The parameter grid is bounds and lists, not rows**, so the form is three labelled blocks:
 * sizes (two comma lists), powers (lowest and highest sphere, with cylinder and axes behind a
 * Toric tick and add behind a Multifocal tick) and the axes it is made in (a preset or a typed
 * list). A tick that is cleared on an edit sends `null` for what it hid, so un-ticking Toric
 * really does clear the cylinder range and the axes.
 *
 * **Everything the server would refuse is refused here first.** A refusal from a queued write
 * halts the queue, and a blocked head stops every write behind it, taking payment included. So
 * 0.25 steps, both-or-neither ranges, ordering, the sign of an add, list shapes, the size of the
 * box and the three-part key are checked before anything is enqueued. `LensCatalogue`'s `num()`
 * is deliberately not used: it returns NaN for text, which serialises as `null`, and on a PATCH
 * `null` means "clear this field".
 *
 * **`already_on_file` is a 201.** A create that meets the existing product writes nothing and says
 * so in `response.outcome`, so the confirmation reads that response and never assumes "created".
 *
 * **The list is always read with the stopped ones included** and the tick hides them here. That
 * is what lets the duplicate check tell "you have this one, stopped" from "you have this one",
 * and it makes un-ticking free.
 *
 * **`cl_product_key` is case-sensitive on (brand, name, modality)**, so "Acuvue" and "ACUVUE" are
 * two rows to the database and one lens to a shopkeeper. The check here ignores case.
 */

/** One product as `GET /catalogue/contact-lens-products` sends it (the flat fields, not `grid`). */
export interface ClProductRow {
  readonly clProductId: string
  readonly brand: string
  readonly name: string
  readonly manufacturer: string | null
  readonly udi: string | null
  readonly modality: string
  readonly material: string | null
  /** A string, as the column holds it. */
  readonly waterContentPercent: string | null
  readonly unitsPerBox: number
  readonly baseCurvesMm: readonly string[] | null
  readonly diametersMm: readonly string[] | null
  readonly sphereMin: number | null
  readonly sphereMax: number | null
  readonly cylinderMin: number | null
  readonly cylinderMax: number | null
  readonly axesDegrees: readonly number[] | null
  readonly addMin: number | null
  readonly addMax: number | null
  readonly substitutionAllowed: boolean
  readonly discontinuedAt: string | null
  readonly version: number
}

export interface ClProductList {
  readonly products: readonly ClProductRow[]
  /** True when more than the server's limit matched. There is no next page. */
  readonly truncated: boolean
}

export interface ClCatalogueProps {
  /** `catalogue_view`. When false the list is not read and the surface says so. */
  readonly canView: boolean
  /** `catalogue_edit`. Gates every button that starts a write. */
  readonly canEdit: boolean
  readonly loadProducts: (query: {
    readonly q: string
    readonly includeDiscontinued: boolean
  }) => Promise<ClProductList>
  /** Called once a write has gone through, so a chooser elsewhere can read the list again. */
  readonly onWritten?: () => void
}

type AxesMode = 'every10' | 'every5' | 'typed'

interface Draft {
  readonly brand: string
  readonly name: string
  readonly modality: string
  readonly unitsPerBox: string
  readonly manufacturer: string
  readonly udi: string
  readonly material: string
  readonly water: string
  readonly baseCurves: string
  readonly diameters: string
  readonly sphereMin: string
  readonly sphereMax: string
  readonly toric: boolean
  readonly cylinderMin: string
  readonly cylinderMax: string
  readonly axesMode: AxesMode
  readonly axesTyped: string
  readonly multifocal: boolean
  readonly addMin: string
  readonly addMax: string
  readonly substitution: boolean
}

const EMPTY: Draft = {
  brand: '',
  name: '',
  modality: '',
  unitsPerBox: '',
  manufacturer: '',
  udi: '',
  material: '',
  water: '',
  baseCurves: '',
  diameters: '',
  sphereMin: '',
  sphereMax: '',
  toric: false,
  cylinderMin: '',
  cylinderMax: '',
  axesMode: 'every10',
  axesTyped: '',
  multifocal: false,
  addMin: '',
  addMax: '',
  substitution: true,
}

/** The server caps the text columns at 200 characters (they sit inside a btree index). */
const MAX_TEXT = 200

const EVERY_10 = Array.from({ length: 18 }, (_, i) => (i + 1) * 10)
const EVERY_5 = Array.from({ length: 36 }, (_, i) => (i + 1) * 5)

const MODALITY_KEYS = {
  daily: 'clRx.modality.daily',
  two_weekly: 'clRx.modality.twoWeekly',
  monthly: 'clRx.modality.monthly',
  quarterly: 'clRx.modality.quarterly',
  annual: 'clRx.modality.annual',
} as const

/** Keys the server names in `differs`, as the words on this screen's own fields. */
const DIFFERS_LABEL = {
  brand: 'clCatalogue.brand',
  name: 'clCatalogue.name',
  modality: 'clCatalogue.modality',
  unitsPerBox: 'clCatalogue.unitsPerBox',
  manufacturer: 'clCatalogue.manufacturer',
  udi: 'clCatalogue.udi',
  material: 'clCatalogue.material',
  waterContentPercent: 'clCatalogue.waterContent',
  baseCurvesMm: 'clCatalogue.baseCurves',
  diametersMm: 'clCatalogue.diameters',
  sphereMin: 'clCatalogue.sphereMin',
  sphereMax: 'clCatalogue.sphereMax',
  cylinderMin: 'clCatalogue.cylinderMin',
  cylinderMax: 'clCatalogue.cylinderMax',
  axesDegrees: 'clCatalogue.axesList',
  addMin: 'clCatalogue.addMin',
  addMax: 'clCatalogue.addMax',
  substitutionAllowed: 'clCatalogue.substitution',
} as const

const sameList = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((value, i) => value === b[i])

const draftOf = (product: ClProductRow): Draft => {
  const axes = product.axesDegrees ?? []
  const axesMode: AxesMode = sameList(axes, EVERY_10)
    ? 'every10'
    : sameList(axes, EVERY_5)
      ? 'every5'
      : 'typed'
  const power = (value: number | null): string => (value === null ? '' : value.toFixed(2))
  return {
    brand: product.brand,
    name: product.name,
    modality: product.modality,
    unitsPerBox: String(product.unitsPerBox),
    manufacturer: product.manufacturer ?? '',
    udi: product.udi ?? '',
    material: product.material ?? '',
    water: product.waterContentPercent ?? '',
    baseCurves: (product.baseCurvesMm ?? []).join(', '),
    diameters: (product.diametersMm ?? []).join(', '),
    sphereMin: power(product.sphereMin),
    sphereMax: power(product.sphereMax),
    toric: axes.length > 0 || product.cylinderMin !== null || product.cylinderMax !== null,
    cylinderMin: power(product.cylinderMin),
    cylinderMax: power(product.cylinderMax),
    axesMode,
    axesTyped: axesMode === 'typed' ? axes.join(', ') : '',
    multifocal: product.addMin !== null || product.addMax !== null,
    addMin: power(product.addMin),
    addMax: power(product.addMax),
    substitution: product.substitutionAllowed,
  }
}

const signed = (value: number): string => `${value > 0 ? '+' : ''}${value.toFixed(2)}`

type Power =
  | { readonly kind: 'empty' }
  | { readonly kind: 'ok'; readonly value: number }
  | { readonly kind: 'shape' }
  | {
      readonly kind: 'step'
      readonly shown: string
      readonly below: number
      readonly above: number
    }

/**
 * A dioptric value typed by a person. Three integer digits and two decimals at most, which is what
 * `numeric(5,2)` holds, then Appendix D's 0.25 step checked on hundredths so no float decides it.
 * Text is `shape`, never NaN.
 */
function parsePower(raw: string): Power {
  const text = raw.trim()
  if (text === '') return { kind: 'empty' }
  if (!/^[+-]?\d{1,3}(\.\d{1,2})?$/.test(text)) return { kind: 'shape' }
  const hundredths = Math.round(Number(text) * 100)
  if (hundredths % 25 !== 0) {
    return {
      kind: 'step',
      shown: text,
      below: (Math.floor(hundredths / 25) * 25) / 100,
      above: (Math.ceil(hundredths / 25) * 25) / 100,
    }
  }
  return { kind: 'ok', value: hundredths === 0 ? 0 : hundredths / 100 }
}

const splitList = (raw: string): string[] =>
  raw
    .split(/[,،]/)
    .map((part) => part.trim())
    .filter((part) => part !== '')

/** Sizes in mm. The server's shape: up to two decimals, above zero, at most 99.99. */
function parseSizes(raw: string): string[] | null {
  const out: string[] = []
  for (const part of splitList(raw)) {
    if (!/^\d{1,2}(\.\d{1,2})?$/.test(part) || Number(part) === 0) return null
    out.push(Number(part).toFixed(2))
  }
  return out
}

function parseAxes(raw: string): number[] | null {
  const out: number[] = []
  for (const part of splitList(raw)) {
    if (!/^\d{1,3}$/.test(part)) return null
    const value = Number(part)
    if (value < 1 || value > 180) return null
    out.push(value)
  }
  return out
}

/**
 * A pasted value with its NUL bytes removed.
 *
 * `replaceAll` with a string rather than a regex, because a control character in a regex literal is
 * what `no-control-regex` exists to catch and this is the one case where it is meant: a NUL reaches
 * Postgres as `22021 invalid byte sequence for encoding UTF8`, which is a 500, and the write queue
 * reads a 500 as unreachable and retries it for ever with every write behind it.
 */
const clean = (value: string): string => value.replaceAll('\u0000', '').trim()
const keyOf = (brand: string, name: string, modality: string): string =>
  `${brand.trim().toLowerCase()}\u0001${name.trim().toLowerCase()}\u0001${modality}`

/** What a queued write was, so the confirmation reads the right part of the response. */
interface Pending {
  readonly kind: 'create' | 'change' | 'other'
  readonly name: string
}

export function ClCatalogue({
  canView,
  canEdit,
  loadProducts,
  onWritten,
}: ClCatalogueProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [products, setProducts] = useState<ClProductList | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [typed, setTyped] = useState('')
  const [query, setQuery] = useState('')
  const [hideStopped, setHideStopped] = useState(true)
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)
  const pending = useRef<Pending | null>(null)
  const requestNo = useRef(0)

  const modalityLabel = (modality: string): string =>
    modality in MODALITY_KEYS ? t(MODALITY_KEYS[modality as keyof typeof MODALITY_KEYS]) : modality

  // A short wait after the last keystroke, so typing a brand is one read and not one per letter.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(typed.trim())
    }, 250)
    return () => {
      clearTimeout(timer)
    }
  }, [typed])

  const refresh = useCallback(() => {
    if (!canView) return
    requestNo.current += 1
    const mine = requestNo.current
    loadProducts({ q: query, includeDiscontinued: true }).then(
      (list) => {
        if (mine !== requestNo.current) return
        setProducts(list)
        setLoadError(false)
      },
      () => {
        if (mine !== requestNo.current) return
        // Its own state, not an empty list: "no contact lens is on file" and "the list could not
        // be read" are different sentences, and only the second is ever true of a failed read.
        setProducts(null)
        setLoadError(true)
      },
    )
  }, [canView, loadProducts, query])

  useEffect(() => {
    refresh()
  }, [refresh])

  const closeForm = (): void => {
    setEditing(null)
    setAdding(false)
    setDraft(EMPTY)
    setError(null)
  }

  useWriteSettled(snapshot, writeId, (write) => {
    const sent = pending.current
    pending.current = null
    const response =
      typeof write.response === 'object' && write.response !== null
        ? (write.response as Record<string, unknown>)
        : {}
    const name = sent?.name ?? ''
    if (sent?.kind === 'create') {
      if (response['outcome'] === 'already_on_file') {
        const differs = Array.isArray(response['differs']) ? (response['differs'] as unknown[]) : []
        const fields = differs
          .map((key) =>
            typeof key === 'string' && key in DIFFERS_LABEL
              ? t(DIFFERS_LABEL[key as keyof typeof DIFFERS_LABEL])
              : null,
          )
          .filter((label): label is string => label !== null)
          .join(', ')
        setDone(
          fields === ''
            ? t('clCatalogue.savedAlready', { name })
            : t('clCatalogue.savedAlreadyDiffers', { name, fields }),
        )
      } else {
        setDone(t('clCatalogue.saved', { name }))
      }
    } else if (sent?.kind === 'change') {
      setDone(
        response['outcome'] === 'unchanged'
          ? t('clCatalogue.unchanged', { name })
          : t('clCatalogue.changed', { name }),
      )
    } else {
      setDone(null)
    }
    setWriteId(null)
    closeForm()
    refresh()
    onWritten?.()
  })

  // A write somebody discarded from the blocked list has to give the form back.
  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [snapshot, writeId])

  const send = useCallback(
    async (kind: Pending['kind'], name: string, request: Parameters<typeof enqueue>[0]) => {
      pending.current = { kind, name }
      setDone(null)
      const write = (await enqueue(request)) as { id: string }
      setWriteId(write.id)
    },
    [enqueue],
  )

  /** Everything the server would refuse, said here first. Returns the body or null. */
  const shaped = (self: ClProductRow | null): Record<string, unknown> | null => {
    const fail = (message: string): null => {
      setError(message)
      return null
    }
    const brand = clean(draft.brand)
    const name = clean(draft.name)
    if (brand === '' || name === '') return fail(t('clCatalogue.brandAndNameRequired'))
    if (!(CONTACT_LENS_MODALITIES as readonly string[]).includes(draft.modality)) {
      return fail(t('clCatalogue.modalityRequired'))
    }
    const box = draft.unitsPerBox.trim()
    if (!/^\d{1,5}$/.test(box) || Number(box) < 1 || Number(box) > 10_000) {
      return fail(t('clCatalogue.unitsPerBoxRequired'))
    }
    const water = draft.water.trim()
    if (water !== '' && (!/^\d{1,3}(\.\d{1,2})?$/.test(water) || Number(water) > 100)) {
      return fail(t('clCatalogue.waterShape'))
    }
    const baseCurves = parseSizes(draft.baseCurves)
    const diameters = parseSizes(draft.diameters)
    if (baseCurves === null || diameters === null) return fail(t('clCatalogue.sizeShape'))

    const power = (raw: string, label: Parameters<typeof t>[0]): number | null | 'bad' => {
      const parsed = parsePower(raw)
      if (parsed.kind === 'empty') return null
      if (parsed.kind === 'ok') return parsed.value
      if (parsed.kind === 'shape') {
        setError(t('clCatalogue.powerShape', { field: t(label) }))
      } else {
        setError(
          t('clCatalogue.stepShape', {
            field: t(label),
            value: parsed.shown,
            below: signed(parsed.below),
            above: signed(parsed.above),
          }),
        )
      }
      return 'bad'
    }
    const sphereMin = power(draft.sphereMin, 'clCatalogue.sphereMin')
    if (sphereMin === 'bad') return null
    const sphereMax = power(draft.sphereMax, 'clCatalogue.sphereMax')
    if (sphereMax === 'bad') return null
    const cylinderMin = draft.toric ? power(draft.cylinderMin, 'clCatalogue.cylinderMin') : null
    if (cylinderMin === 'bad') return null
    const cylinderMax = draft.toric ? power(draft.cylinderMax, 'clCatalogue.cylinderMax') : null
    if (cylinderMax === 'bad') return null
    const addMin = draft.multifocal ? power(draft.addMin, 'clCatalogue.addMin') : null
    if (addMin === 'bad') return null
    const addMax = draft.multifocal ? power(draft.addMax, 'clCatalogue.addMax') : null
    if (addMax === 'bad') return null

    for (const [lo, hi, what] of [
      [sphereMin, sphereMax, t('jobDetail.qc.field.sphere')],
      [cylinderMin, cylinderMax, t('jobDetail.qc.field.cylinder')],
      [addMin, addMax, t('jobDetail.qc.field.add')],
    ] as const) {
      if ((lo === null) !== (hi === null)) return fail(t('clCatalogue.rangeBoth', { what }))
      if (lo !== null && hi !== null && hi < lo) {
        return fail(t('clCatalogue.rangeReversed', { what }))
      }
    }
    if ((addMin !== null && addMin < 0) || (addMax !== null && addMax < 0)) {
      return fail(t('clCatalogue.addNegative'))
    }

    let axes: number[] | null = null
    if (draft.toric) {
      axes =
        draft.axesMode === 'every10'
          ? EVERY_10
          : draft.axesMode === 'every5'
            ? EVERY_5
            : parseAxes(draft.axesTyped)
      if (axes === null) return fail(t('clCatalogue.axisShape'))
      if (axes.length === 0) return fail(t('clCatalogue.toricNeedsAxes'))
    }

    const clash = (products?.products ?? []).find(
      (candidate) =>
        (self === null || candidate.clProductId !== self.clProductId) &&
        keyOf(candidate.brand, candidate.name, candidate.modality) ===
          keyOf(brand, name, draft.modality),
    )
    if (clash !== undefined) {
      return fail(
        self !== null
          ? t('clCatalogue.renameClash')
          : clash.discontinuedAt !== null
            ? t('clCatalogue.alreadyStopped', { name: clash.name })
            : t('clCatalogue.alreadyOnFile', { name: clash.name }),
      )
    }

    setError(null)
    // `null` clears and absent leaves alone, so an edit that empties a box has to say so.
    const orNull = (value: string): string | null => (clean(value) === '' ? null : clean(value))
    return {
      brand,
      name,
      modality: draft.modality,
      unitsPerBox: Number(box),
      manufacturer: orNull(draft.manufacturer),
      udi: orNull(draft.udi),
      material: orNull(draft.material),
      waterContentPercent: water === '' ? null : water,
      baseCurvesMm: baseCurves.length === 0 ? null : baseCurves,
      diametersMm: diameters.length === 0 ? null : diameters,
      sphereMin,
      sphereMax,
      cylinderMin,
      cylinderMax,
      axesDegrees: axes,
      addMin,
      addMax,
    }
  }

  const save = async (self: ClProductRow | null): Promise<void> => {
    const body = shaped(self)
    if (body === null) return
    const name = String(body['name'])
    if (self === null) {
      await send('create', name, {
        method: 'POST',
        path: '/catalogue/contact-lens-products',
        label: t('clCatalogue.queueAddLabel', { name }),
        body: { ...body, substitutionAllowed: draft.substitution },
      })
    } else {
      await send('change', name, {
        method: 'PATCH',
        path: `/catalogue/contact-lens-products/${encodeURIComponent(self.clProductId)}`,
        label: t('clCatalogue.queueChangeLabel', { name: self.name }),
        body: { ...body, expectedVersion: self.version },
      })
    }
  }

  const setStopped = async (product: ClProductRow, stop: boolean): Promise<void> => {
    setError(null)
    await send('other', product.name, {
      method: 'POST',
      path: `/catalogue/contact-lens-products/${encodeURIComponent(product.clProductId)}/${
        stop ? 'discontinue' : 'reinstate'
      }`,
      label: t(stop ? 'clCatalogue.queueStopLabel' : 'clCatalogue.queueResumeLabel', {
        name: product.name,
      }),
      body: { expectedVersion: product.version },
    })
  }

  const setSubstitution = async (product: ClProductRow, allowed: boolean): Promise<void> => {
    setError(null)
    await send('other', product.name, {
      method: 'PATCH',
      path: `/catalogue/contact-lens-products/${encodeURIComponent(product.clProductId)}/substitution`,
      label: t('clCatalogue.queueSubstitutionLabel', { name: product.name }),
      body: { allowed, expectedVersion: product.version },
    })
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => {
    setDraft((was) => ({ ...was, [key]: value }))
  }

  const text = (
    key: 'brand' | 'name' | 'manufacturer' | 'udi' | 'material',
    label: Parameters<typeof t>[0],
    options: { readonly ltr?: boolean; readonly hint?: Parameters<typeof t>[0] } = {},
  ): JSX.Element => (
    <div className="lm-field">
      <label className="lm-field__label" htmlFor={`clc-${key}`}>
        {t(label)}
      </label>
      <input
        id={`clc-${key}`}
        className="lm-input"
        dir={options.ltr === true ? 'ltr' : 'auto'}
        autoComplete="off"
        maxLength={MAX_TEXT}
        value={draft[key]}
        onChange={(e) => {
          set(key, e.currentTarget.value)
        }}
      />
      {options.hint === undefined ? null : <p className="lm-hint">{t(options.hint)}</p>}
    </div>
  )

  const number = (
    key:
      | 'unitsPerBox'
      | 'water'
      | 'baseCurves'
      | 'diameters'
      | 'sphereMin'
      | 'sphereMax'
      | 'cylinderMin'
      | 'cylinderMax'
      | 'addMin'
      | 'addMax'
      | 'axesTyped',
    label: Parameters<typeof t>[0],
    placeholder?: string,
  ): JSX.Element => (
    <div className="lm-field">
      <label className="lm-field__label" htmlFor={`clc-${key}`}>
        {t(label)}
      </label>
      <input
        id={`clc-${key}`}
        className="lm-input"
        dir="ltr"
        inputMode="decimal"
        autoComplete="off"
        {...(placeholder === undefined ? {} : { placeholder })}
        value={draft[key]}
        onChange={(e) => {
          set(key, e.currentTarget.value)
        }}
      />
    </div>
  )

  const tick = (
    key: 'toric' | 'multifocal' | 'substitution',
    label: Parameters<typeof t>[0],
  ): JSX.Element => (
    <div className="lm-field">
      <input
        id={`clc-${key}`}
        type="checkbox"
        checked={draft[key]}
        onChange={(e) => {
          set(key, e.currentTarget.checked)
        }}
      />
      <label htmlFor={`clc-${key}`}>{t(label)}</label>
    </div>
  )

  const form = (self: ClProductRow | null): JSX.Element => (
    <div className="lm-supplier__form">
      {text('brand', 'clCatalogue.brand')}
      {text('name', 'clCatalogue.name')}
      <div className="lm-field">
        <label className="lm-field__label" htmlFor="clc-modality">
          {t('clCatalogue.modality')}
        </label>
        <select
          id="clc-modality"
          className="lm-input"
          value={draft.modality}
          onChange={(e) => {
            set('modality', e.currentTarget.value)
          }}
        >
          <option value="">{t('clCatalogue.modalityChoose')}</option>
          {CONTACT_LENS_MODALITIES.map((value) => (
            <option key={value} value={value}>
              {modalityLabel(value)}
            </option>
          ))}
        </select>
      </div>
      {number('unitsPerBox', 'clCatalogue.unitsPerBox')}
      <p className="lm-hint">{t('clCatalogue.unitsPerBoxMeans')}</p>
      {text('manufacturer', 'clCatalogue.manufacturer')}
      {text('udi', 'clCatalogue.udi', { ltr: true })}
      {text('material', 'clCatalogue.material', { hint: 'clCatalogue.materialHint' })}
      {number('water', 'clCatalogue.waterContent')}

      <fieldset className="lm-fieldset">
        <legend className="lm-mira__legend">{t('clCatalogue.sizesHeading')}</legend>
        <p className="lm-hint">{t('clCatalogue.sizesMeans')}</p>
        {number('baseCurves', 'clCatalogue.baseCurves', '8.4, 8.7')}
        {number('diameters', 'clCatalogue.diameters', '14.0, 14.2')}
      </fieldset>

      <fieldset className="lm-fieldset">
        <legend className="lm-mira__legend">{t('clCatalogue.powersHeading')}</legend>
        <p className="lm-hint">{t('clCatalogue.powersMeans')}</p>
        {number('sphereMin', 'clCatalogue.sphereMin', '-10.00')}
        {number('sphereMax', 'clCatalogue.sphereMax', '+8.00')}
        {tick('toric', 'clCatalogue.isToric')}
        {draft.toric ? (
          <>
            <p className="lm-hint">{t('clCatalogue.cylinderMeans')}</p>
            {number('cylinderMin', 'clCatalogue.cylinderMin', '-2.25')}
            {number('cylinderMax', 'clCatalogue.cylinderMax', '-0.75')}
            <div className="lm-field">
              <label className="lm-field__label" htmlFor="clc-axesMode">
                {t('clCatalogue.axesMode')}
              </label>
              <select
                id="clc-axesMode"
                className="lm-input"
                value={draft.axesMode}
                onChange={(e) => {
                  set('axesMode', e.currentTarget.value as AxesMode)
                }}
              >
                <option value="every10">{t('clCatalogue.axesEvery10')}</option>
                <option value="every5">{t('clCatalogue.axesEvery5')}</option>
                <option value="typed">{t('clCatalogue.axesTyped')}</option>
              </select>
            </div>
            {draft.axesMode === 'typed'
              ? number('axesTyped', 'clCatalogue.axesList', '90, 180')
              : null}
          </>
        ) : null}
        {tick('multifocal', 'clCatalogue.isMultifocal')}
        {draft.multifocal ? (
          <>
            {number('addMin', 'clCatalogue.addMin', '+0.75')}
            {number('addMax', 'clCatalogue.addMax', '+2.50')}
          </>
        ) : null}
      </fieldset>

      {self === null ? (
        <>
          {tick('substitution', 'clCatalogue.substitution')}
          <p className="lm-hint">{t('clCatalogue.substitutionMeans')}</p>
        </>
      ) : null}

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void save(self)
        }}
      >
        {t(self === null ? 'clCatalogue.save' : 'clCatalogue.saveChange')}
      </button>
      <button type="button" className="lm-button lm-button--secondary" onClick={closeForm}>
        {t('clCatalogue.cancel')}
      </button>
    </div>
  )

  const range = (
    key: 'clCatalogue.row.sphere' | 'clCatalogue.row.cylinder' | 'clCatalogue.row.add',
    min: number | null,
    max: number | null,
  ): JSX.Element | null =>
    min === null || max === null ? null : (
      <span className="lm-supplier__meta">
        {sentence(t, key, {
          min: <Ltr>{signed(min)}</Ltr>,
          max: <Ltr>{signed(max)}</Ltr>,
        })}
      </span>
    )

  const list = (
    key: 'clCatalogue.row.baseCurve' | 'clCatalogue.row.diameter' | 'clCatalogue.row.axes',
    values: readonly (string | number)[] | null,
  ): JSX.Element | null =>
    values === null || values.length === 0 ? null : (
      <span className="lm-supplier__meta">
        {sentence(t, key, { values: <Ltr>{values.join(', ')}</Ltr> })}
      </span>
    )

  const all = products?.products ?? []
  const shown = hideStopped ? all.filter((product) => product.discontinuedAt === null) : all
  const onlyStopped = all.length > 0 && shown.length === 0

  const line = statusLine(t, snapshot, writeId, t('queue.subject.clProduct'))

  return (
    <section className="lm-screen" aria-labelledby="cl-catalogue-heading">
      <h1 id="cl-catalogue-heading" className="lm-screen__heading">
        {t('clCatalogue.heading')}
      </h1>
      <p className="lm-hint">{t('clCatalogue.means')}</p>

      {canView ? (
        <>
          <div className="lm-field">
            <label className="lm-field__label" htmlFor="clc-search">
              {t('clCatalogue.searchLabel')}
            </label>
            <input
              id="clc-search"
              className="lm-input"
              type="search"
              dir="auto"
              autoComplete="off"
              placeholder={t('clCatalogue.searchHint')}
              value={typed}
              onChange={(e) => {
                setTyped(e.currentTarget.value)
              }}
            />
          </div>
          <div className="lm-field">
            <input
              id="clc-hide-stopped"
              type="checkbox"
              checked={hideStopped}
              onChange={(e) => {
                setHideStopped(e.currentTarget.checked)
              }}
            />
            <label htmlFor="clc-hide-stopped">{t('clCatalogue.hideStopped')}</label>
          </div>
        </>
      ) : null}

      {/*
        §8 `ST-02`. The add button stays outside the surface so a shop with none, or a list that
        failed to read, still has the way to add one. Reached states: denied, loading, error,
        empty, filtered and partial (the server stopped at its limit).
      */}
      <DataSurface
        state={
          !canView
            ? { kind: 'denied' }
            : loadError
              ? { kind: 'error', message: t('clCatalogue.loadFailed'), onRetry: refresh }
              : products === null
                ? { kind: 'loading' }
                : all.length === 0
                  ? query === ''
                    ? { kind: 'empty' }
                    : { kind: 'filtered' }
                  : shown.length === 0
                    ? { kind: 'filtered' }
                    : products.truncated
                      ? {
                          kind: 'partial',
                          missing: t('clCatalogue.truncated', { count: all.length }),
                        }
                      : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('clCatalogue.what')}
        emptyMessage={canEdit ? t('clCatalogue.none') : t('clCatalogue.noneReadOnly')}
        filteredMessage={onlyStopped ? t('clCatalogue.filteredRetired') : t('clCatalogue.filtered')}
        deniedMessage={t('clCatalogue.denied')}
      >
        <ul className="lm-supplier-list">
          {shown.map((product) => (
            <li key={product.clProductId} className="lm-supplier">
              <span className="lm-supplier__name" dir="auto">
                {product.brand} {product.name}
              </span>
              <span className="lm-supplier__meta">
                {modalityLabel(product.modality)}
                {' · '}
                {/* A plural message: `count` has to be the number itself, not an element. */}
                {t('clCatalogue.row.box', { count: product.unitsPerBox })}
                {product.discontinuedAt === null ? '' : ` · ${t('clCatalogue.row.stopped')}`}
              </span>
              {product.sphereMin === null && product.sphereMax === null ? (
                <span className="lm-supplier__meta">{t('clCatalogue.row.noPowers')}</span>
              ) : (
                range('clCatalogue.row.sphere', product.sphereMin, product.sphereMax)
              )}
              {range('clCatalogue.row.cylinder', product.cylinderMin, product.cylinderMax)}
              {range('clCatalogue.row.add', product.addMin, product.addMax)}
              {list('clCatalogue.row.baseCurve', product.baseCurvesMm)}
              {list('clCatalogue.row.diameter', product.diametersMm)}
              {list('clCatalogue.row.axes', product.axesDegrees)}
              <span className="lm-supplier__meta">
                {product.substitutionAllowed
                  ? t('clCatalogue.row.substitutionAllowed')
                  : t('clCatalogue.row.brandOnly')}
              </span>
              {canEdit && editing === product.clProductId ? (
                form(product)
              ) : canEdit ? (
                <>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      setAdding(false)
                      setEditing(product.clProductId)
                      setDraft(draftOf(product))
                      setError(null)
                    }}
                  >
                    {t('clCatalogue.change', { name: product.name })}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={writeId !== null}
                    onClick={() => {
                      void setStopped(product, product.discontinuedAt === null)
                    }}
                  >
                    {t(
                      product.discontinuedAt === null ? 'clCatalogue.stop' : 'clCatalogue.resume',
                      {
                        name: product.name,
                      },
                    )}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={writeId !== null}
                    onClick={() => {
                      void setSubstitution(product, !product.substitutionAllowed)
                    }}
                  >
                    {t(
                      product.substitutionAllowed
                        ? 'clCatalogue.forbidSubstitution'
                        : 'clCatalogue.allowSubstitution',
                      { name: product.name },
                    )}
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </DataSurface>

      {canEdit ? (
        adding ? (
          form(null)
        ) : (
          <button
            type="button"
            className="lm-button lm-button--primary"
            onClick={() => {
              setEditing(null)
              setAdding(true)
              setDraft(EMPTY)
              setError(null)
              setDone(null)
            }}
          >
            {t('clCatalogue.addOne')}
          </button>
        )
      ) : null}

      {done === null ? null : (
        <p className="lm-hint" role="status">
          {done}
        </p>
      )}
      {line === null || !canView ? null : <p className="lm-hint">{line}</p>}
    </section>
  )
}
